import React from 'react';
import { useAppSelector } from '../../store';
import { uniqueId } from 'lodash-es';

import craftJsParser from '../../components/utils/craftJsParser';
import transferPropertiesToComponent from '../ComponentData/transferPropertiesToComponent';
import { DataElementContext } from '../common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { useMediaQuery } from '@/components/utils/useQueryMedia';
import { PageDataContext } from '@/components/utils/PageDataProvider';

export const defaultProps = {
  properties: {
    componentId: null,
  },
  visibility: {},
};

export const ComponentRef = (componentProps) => {
  let props = componentProps;

  const [__id] = React.useState(uniqueId('unique-'));
  const components = useAppSelector((state) => state.templatesConfig.components);
  const setComponentsDefaults = useAppSelector((state) => state.templatesConfig.setComponentsDefaults);
  const authenticated = useAppSelector((state) => state.authentication && state.authentication.auth_type === 'user');

  const pageDataContext = React.useContext(PageDataContext);
  const dataElementContext = React.useContext(DataElementContext);
  let isVisible = true;

  const isTablet = useMediaQuery(`(min-width: ${window.config.breakpointTablet})`);
  const isDesktop = useMediaQuery(`(min-width: ${window.config.breakpointDesktop})`);
  const breakpoints = React.useMemo(() => ({ isTablet, isDesktop }), [isTablet, isDesktop]);

  [props, isVisible] = processComponentProps(props, dataElementContext, breakpoints);

  const { componentId } = props.properties;

  let [content, skeleton] = React.useMemo(() => {
    try {
      //if (componentId === '1f50dd7d-3c6d-4407-b4a6-261af74bd856') {
      //  debugger;
      //const a = 1;
      //}
      const projectData = components[componentId].projectData;

      const setComponentsDefaultsValue =
        setComponentsDefaults[componentId] != null ? setComponentsDefaults[componentId] : {};

      const data = JSON.parse(projectData);

      //console.log('ComponentRef[props]', props);

      if (props.slotChildren?.children?.length) {
        Object.keys(data).forEach(key => {
          if (data[key].type === 'ChildrenSlot') {
            data[key].nodes = props.slotChildren?.children;
          }
        });

        props.slotChildren?.childrenCraftJsNodes?.forEach?.((node) => {
          if (!data[node.id]) {
            data[node.id] = node;
          }
        });
      }

      if (props.slotChildren?.linkedNodes) {
        const linkedNodes = props.slotChildren.linkedNodes;

        Object.keys(data).forEach(key => {
          if (data[key].type === 'NamedSlot') {
            if (linkedNodes?.[`NamedSlot-${data[key].id}`]) {
              const node = props.slotChildren?.linkedCraftJsNodes?.find?.(n => n.id === linkedNodes[`NamedSlot-${data[key].id}`]);
              if (node) {
                data[key].nodes = node.nodes;
              }
            }
          }
        });

        props.slotChildren?.linkedCraftJsNodes?.forEach?.((node) => {
          if (!data[node.id]) {
            data[node.id] = node;
          }
        });
      }

      //console.log('ComponentRef[data]', data);

      //if (componentId === '1f50dd7d-3c6d-4407-b4a6-261af74bd856') {
      //  console.log('ComponentRef[aboveTheFold] setComponentsDefaultsValue', setComponentsDefaultsValue);
      //}

      if (setComponentsDefaultsValue && Object.keys(setComponentsDefaultsValue).length)
        transferPropertiesToComponent(data, setComponentsDefaultsValue);

      //if (componentId === '1f50dd7d-3c6d-4407-b4a6-261af74bd856') {
      //  console.log('ComponentRef[aboveTheFold] properties', props.properties);
      //}

      const skipProps = ['componentId', 'dsType', 'propFunction', 'links', 'megaComponent', '-reLinkNodes-'];
      props?.properties && Object.keys(props.properties).forEach(key => {
        if (skipProps.indexOf(key) > -1) return;
        data['ROOT'].props.properties[key] = props.properties[key];
      });

      // ensure that overrides from page layout and page are still applied the above calls may have overwritten them
      if (props.plov && Object.keys(props.plov).length)
        transferPropertiesToComponent(data, props.plov);
      if (props.pov && Object.keys(props.pov).length)
        transferPropertiesToComponent(data, props.pov);

      const skeleton = data['ROOT'].props.properties;

      //if (componentId === '1f50dd7d-3c6d-4407-b4a6-261af74bd856') {
      //  console.log('ComponentRef[aboveTheFold] ROOT.properties', data['ROOT'].props.properties);
      //}

      let rootNodeId = 'ROOT';

      if (rootNodeId) {
        data[rootNodeId].type = 'Fragment';
        data[rootNodeId].props = {
          'className': componentProps.className ?? '',
          'properties': {
            ...data[rootNodeId].props?.properties,
            'data-cmp-id': componentId,
          },
        };

        if (data[rootNodeId].nodes && data[rootNodeId].nodes.length === 1) {
          rootNodeId = data[rootNodeId].nodes[0];
        }

        if (props?.properties?.['-reLinkNodes-']) {
          const rln = props.properties['-reLinkNodes-'];
          Object.keys(rln ?? {}).forEach(key => {
            if (rln[key] && data?.[key]) {
              const links = data[key].props?.properties?.links ?? {};
              if (!data[key].props) {
                data[key].props = { properties: {} };
              } else if (!data[key].props.properties) {
                data[key].props.properties = {};
              }
              data[key].props.properties.links = {
                ...links,
                ...rln[key],
              };
            }
          });
        }

        if (!data[rootNodeId]?.props?.properties) {
          data[rootNodeId].props.properties = {};
        }

        if (window !== window.parent) {
          data[rootNodeId].props.properties['data-cjs-n-id'] = componentProps.id;
        }
        data[rootNodeId].props.properties['data-cmp-id'] = componentId;

        return [craftJsParser({
          craftState: data,
          rootNodeId: rootNodeId,
          pageId: componentId,
          pageType: 'components',
          pageProperties: props.properties,
          options: {
            authenticated,
          },
          withProvider: true,
          children: componentProps.children ?? null,
          parentPageDataContext: pageDataContext,
          parentDataElementContext: dataElementContext,
        }), skeleton];
      }
    } catch (err) {
      console.error(`ComponentRef[id:${componentId}]:`, err);
    }
    return [null, null];
  }, [
    components, setComponentsDefaults, componentId, authenticated, componentProps, pageDataContext, dataElementContext,
    props.slotChildren, props.properties, props.plov, props.pov
  ]);

  const contextValue = React.useMemo(() => {
    let contextValue = {};

    skeleton && Object.keys(skeleton).forEach(key => {
      contextValue[key] = skeleton[key];
    });

    contextValue = {
      ...dataElementContext,
      ...contextValue,
      __id,
    };
    return contextValue;
  }, [componentProps, skeleton, dataElementContext, components, setComponentsDefaults, componentId, authenticated, __id, props.properties]); // eslint-disable-line

  //if (componentId === '1f50dd7d-3c6d-4407-b4a6-261af74bd856') {
  //  console.log('ComponentRef[aboveTheFold] contextValue', contextValue);
  //}

  if (!isVisible) return null;

  /*
  if (props.properties.componentId === '847e5620-1891-4e43-90ea-a29a3cb28d9a') {
    console.log(`ComponentRef[${props.id}] context`, JSON.parse(JSON.stringify(contextValue)), props.properties);
  }
  */

  return <DataElementContext.Provider value={contextValue}>{content}</DataElementContext.Provider>;
};
